export {};

const INIT_FLAG = 'stgbAccordionInit';
const OPEN_CLASS = 'is-open';
// Fallback for browsers that don't fire transitionend on grid-template-rows
// (or when motion is reduced) — keep it a touch longer than the CSS duration.
const COLLAPSE_FALLBACK_MS = 500;

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const initBlock = (block: HTMLElement) => {
    if (block.dataset[INIT_FLAG] === 'true') return;

    const allowMultiple = block.dataset.allowMultiple === 'true';
    const items = Array.from(
        block.querySelectorAll<HTMLElement>('.stgb-accordion-item')
    );

    // Cleanup for an in-flight close, so re-opening mid-animation can cancel
    // the pending "set hidden" without it clobbering the freshly opened panel.
    const pendingClose = new WeakMap<HTMLElement, () => void>();

    const triggerFor = (item: HTMLElement) =>
        item.querySelector<HTMLButtonElement>('.stgb-accordion-trigger');
    const panelFor = (item: HTMLElement) =>
        item.querySelector<HTMLElement>('.stgb-accordion-panel');

    const isOpen = (item: HTMLElement) => item.classList.contains(OPEN_CLASS);

    const cancelPendingClose = (item: HTMLElement) => {
        const cancel = pendingClose.get(item);
        if (cancel) {
            cancel();
            pendingClose.delete(item);
        }
    };

    const openItem = (item: HTMLElement) => {
        const trigger = triggerFor(item);
        const panel = panelFor(item);
        if (!trigger || !panel) return;

        cancelPendingClose(item);
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');

        if (prefersReducedMotion()) {
            item.classList.add(OPEN_CLASS);
            return;
        }
        // Let the panel paint at 0fr before flipping to 1fr so the grid-rows
        // transition has a starting point to animate from.
        requestAnimationFrame(() => item.classList.add(OPEN_CLASS));
    };

    const closeItem = (item: HTMLElement) => {
        const trigger = triggerFor(item);
        const panel = panelFor(item);
        if (!trigger || !panel) return;

        cancelPendingClose(item);
        item.classList.remove(OPEN_CLASS);
        trigger.setAttribute('aria-expanded', 'false');

        if (prefersReducedMotion()) {
            panel.hidden = true;
            return;
        }

        let done = false;
        const onEnd = (event: TransitionEvent) => {
            if (event.target === panel && event.propertyName === 'grid-template-rows') {
                finish();
            }
        };
        const cleanup = () => {
            panel.removeEventListener('transitionend', onEnd);
            window.clearTimeout(timer);
            pendingClose.delete(item);
        };
        const finish = () => {
            if (done) return;
            done = true;
            cleanup();
            panel.hidden = true;
        };
        const timer = window.setTimeout(finish, COLLAPSE_FALLBACK_MS);

        // Re-opening calls this to bail out without hiding the panel.
        pendingClose.set(item, () => {
            done = true;
            cleanup();
        });
        panel.addEventListener('transitionend', onEnd);
    };

    const toggleItem = (item: HTMLElement) => {
        if (isOpen(item)) {
            closeItem(item);
            return;
        }
        if (!allowMultiple) {
            items.forEach((other) => {
                if (other !== item && isOpen(other)) closeItem(other);
            });
        }
        openItem(item);
    };

    items.forEach((item) => {
        const trigger = triggerFor(item);
        if (!trigger) return;
        trigger.addEventListener('click', () => toggleItem(item));
    });

    block.dataset[INIT_FLAG] = 'true';
};

const initAll = () => {
    document
        .querySelectorAll<HTMLElement>('.stgb-accordion')
        .forEach(initBlock);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}
